import { useCallback, useEffect, useState } from 'react';
import { api, type BillingMonth, type Company, type Fee } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, money, period as writtenPeriod, periodShort } from '../format';
import * as Icon from '../components/Icons';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import { PaymentMethods } from '../components/PaymentMethods';
import type { Panel } from '../components/Shell';

/** Domyślnie poprzedni miesiąc — ten, który właśnie się rozlicza. */
function previousMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
}

export function BillingPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
}) {
  const [month, setMonth] = useState(previousMonth);
  const [preview, setPreview] = useState<Fee | null>(null);
  const [history, setHistory] = useState<BillingMonth[] | null>(null);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [fee, months] = await Promise.all([api.billingPreview(company.id, month), api.billing(company.id)]);
      setPreview(fee);
      setHistory(months);
    } catch (failure) {
      setPreview(null);
      setError(failureOf(failure));
    }
  }, [company.id, month]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Opłata wobec limitu 20% przychodu. Pasek pokazuje jedno: jak daleko opłacie do sufitu.
   * Przy przychodzie 20 500 zł i opłacie 60,27 zł to mniej niż jedna trzysta — i o to chodzi,
   * żeby to było widać, zamiast porównywać dwie liczby w akapicie.
   */
  const revenue = Number(preview?.revenue ?? 0);
  const fee = Number(preview?.planPriceGross ?? 0);
  const share = revenue > 0 ? Math.min(100, (fee / revenue) * 100) : 0;

  // Rozpisanie musi się zsumować do kwoty do zapłaty, także gdy zniżka zbije opłatę
  // poniżej progu 5 zł i miesiąc wychodzi bezpłatny
  const lower = Math.min(fee, Number(preview?.revenueCapGross ?? 0));
  const discount = lower * Number(preview?.multiCompanyDiscount ?? 0);
  const afterDiscount = lower - discount;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.billing.title}</h3>
          <p>{t.billing.lead}</p>
        </div>
        <div className="sp">
          <input
            className="inp w"
            type="month"
            aria-label={t.billing.month}
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {preview && (
        <>
          <div className="split">
            <div className="panel metric">
              <span className="k">
                {t.billing.revenue} · {writtenPeriod(month, locale)}
              </span>
              <span className="v num">{money(preview.revenue, locale)}</span>
              <span className="sub">{t.billing.revenueSub}</span>
            </div>
            <div className="panel metric">
              <span className="k">{t.billing.planPrice}</span>
              <span className="v num">{money(preview.planPriceGross, locale)}</span>
              <span className="sub">{t.billing.plans[preview.plan]}</span>
            </div>
            <div className="panel metric">
              <span className="k">{t.billing.cap}</span>
              <span className="v num">{money(preview.revenueCapGross, locale)}</span>
            </div>
            <div className="panel metric lead">
              <span className="k">{t.billing.toPay}</span>
              <span className="v num">{money(preview.feeGross, locale)}</span>
              {/* Zero ma zawsze powód — inaczej klient nie wie, czy to prezent, czy błąd */}
              {preview.zeroReason && <span className="sub">{t.billing.reasons[preview.zeroReason]}</span>}
            </div>
          </div>

          <div className="panel">
            <header>
              <h4>{t.billing.howMuch}</h4>
            </header>
            <div className="capbar">
              <span className="k" style={{ width: 130 }}>
                {writtenPeriod(month, locale)}
              </span>
              <div className="track">
                <div className="fee" style={{ width: `${Math.max(share, 0.3)}%` }} />
                <div className="cap" style={{ left: '20%' }} />
                <span className="lbl2">
                  {t.billing.planPrice} {money(preview.planPriceGross, locale)} · {t.billing.cap}{' '}
                  {money(preview.revenueCapGross, locale)}
                </span>
              </div>
              <span className="num" style={{ fontWeight: 'var(--font-weight-semibold)', flex: '0 0 110px', textAlign: 'right' }}>
                {money(preview.feeGross, locale)}
              </span>
            </div>
            <dl className="brk" style={{ maxWidth: 520 }}>
              <dt>{t.billing.planPrice}</dt>
              <dd>{money(preview.planPriceGross, locale)}</dd>
              <dt>{t.billing.capOf(money(preview.revenue, locale))}</dt>
              <dd>{money(preview.revenueCapGross, locale)}</dd>
              <dt>{t.billing.lower}</dt>
              <dd>{money(lower, locale)}</dd>
              {/* Zniżka za kilka działalności — bez tego wiersza rachunek się nie zgadza */}
              {Number(preview.multiCompanyDiscount) > 0 && (
                <>
                  <dt>
                    {t.billing.multiCompany(preview.payingCompanies)} ·{' '}
                    {Math.round(Number(preview.multiCompanyDiscount) * 100)}%
                  </dt>
                  <dd>−{money(discount, locale)}</dd>
                </>
              )}
              {preview.zeroReason && (
                <>
                  <dt>{t.billing.reasons[preview.zeroReason]}</dt>
                  <dd>−{money(afterDiscount, locale)}</dd>
                </>
              )}
              <dt className="tot">{t.billing.toPay}</dt>
              <dd className="tot">{money(preview.feeGross, locale)}</dd>
            </dl>
          </div>
        </>
      )}

      <div className="grid two">
        <div className="panel">
          <header>
            <h4>{t.billing.history}</h4>
          </header>
          {history?.length === 0 && (
            <div className="empty">
              <b>{t.billing.none}</b>
            </div>
          )}
          {history && history.length > 0 && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.billing.month}</th>
                    <th className="n">{t.billing.revenue}</th>
                    <th className="n">{t.billing.charged}</th>
                    <th>{t.billing.note}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(({ record, adjustments }) => (
                    <tr key={record.id}>
                      <td className="doc">{periodShort(record.period)}</td>
                      <td className="n">{amount(record.revenue, locale)}</td>
                      <td className="n">{amount(record.feeGross, locale)}</td>
                      <td className="muted sm">
                        {record.zeroReason && t.billing.reasons[record.zeroReason]}
                        {adjustments.map((adjustment) => (
                          <div key={adjustment.id}>
                            {adjustment.kind === 'SURCHARGE' ? t.billing.surcharge : t.billing.credit}:{' '}
                            {amount(adjustment.amount, locale)}{' '}
                            <span className="chip st-draft">
                              <i />
                              {t.billing.statuses[adjustment.status]}
                            </span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {preview && (
          <div className="panel">
            <header>
              <h4>{t.billing.plan}</h4>
            </header>
            <dl className="brk">
              <dt>{t.billing.plan}</dt>
              <dd>{t.billing.plans[preview.plan]}</dd>
              <dt>{t.billing.payer}</dt>
              <dd>{t.billing.payers[preview.payer]}</dd>
              <dt>{t.billing.channel}</dt>
              <dd>{t.billing.channels[preview.channel]}</dd>
            </dl>
            <div className="alert a-ok">
              <Icon.Check />
              <div>
                <b>{t.billing.noRevenueTitle}</b>
                <p>{t.billing.noRevenueText}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Za miesiące opłacane przez biuro płaci biuro — firma nie potrzebuje wtedy własnej zgody */}
      {preview?.payer === 'OFFICE' ? (
        <div className="alert a-ok">
          <Icon.Info />
          <div>
            <b>{t.payments.officePays}</b>
          </div>
        </div>
      ) : (
        <PaymentMethods
          scope={{ kind: 'companies', id: company.id }}
          canManage={company.roles.includes('OWNER') || company.roles.includes('ADMIN')}
          t={t}
          locale={locale}
        />
      )}
    </>
  );
}
